// ↑ ↓ and a ⋯ menu (Move to top / Move to bottom) for one row of a flat content
// list (plan R6 §2.1). Disabled while a move is saving, and in mixed views with
// a hint saying where the order can be changed.
import { useState } from 'react';
import { IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { MoveKind, canMove } from './reorderModel';

export type MoveLabels = Record<MoveKind, string>;

export const DEFAULT_MOVE_LABELS: MoveLabels = {
  up: 'Move up',
  down: 'Move down',
  top: 'Move to top',
  bottom: 'Move to bottom',
};

type Props = {
  // The row's name, for the aria labels.
  name: string;
  index: number;
  count: number;
  onMove: (kind: MoveKind) => void;
  disabled?: boolean;
  // Shown as the tooltip when disabled (e.g. "Pick a section to change its order").
  disabledHint?: string;
  labels?: MoveLabels;
};

export default function ReorderControls({
  name,
  index,
  count,
  onMove,
  disabled = false,
  disabledHint,
  labels = DEFAULT_MOVE_LABELS,
}: Props) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  // "Move up" → "Move Students up".
  const label = (kind: MoveKind) => labels[kind].replace(/^Move\b/, () => `Move ${name}`);
  const off = (kind: MoveKind) => disabled || !canMove(kind, index, count);
  const tip = (kind: MoveKind) => (disabled && disabledHint ? disabledHint : labels[kind]);
  const pick = (kind: MoveKind) => {
    setMenu(null);
    onMove(kind);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0}>
      {(['up', 'down'] as MoveKind[]).map((kind) => (
        <Tooltip key={kind} title={tip(kind)}>
          <span>
            <IconButton
              size="small"
              disabled={off(kind)}
              onClick={() => onMove(kind)}
              aria-label={label(kind)}
            >
              <Iconify icon={kind === 'up' ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'} />
            </IconButton>
          </span>
        </Tooltip>
      ))}
      <Tooltip title={disabled && disabledHint ? disabledHint : 'More order actions'}>
        <span>
          <IconButton
            size="small"
            disabled={disabled || count < 2}
            onClick={(e) => setMenu(e.currentTarget)}
            aria-label={`More order actions for ${name}`}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem disabled={off('top')} onClick={() => pick('top')}>
          {labels.top}
        </MenuItem>
        <MenuItem disabled={off('bottom')} onClick={() => pick('bottom')}>
          {labels.bottom}
        </MenuItem>
      </Menu>
    </Stack>
  );
}
